import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { AssignDepositPanel } from '../components/deposits/AssignDepositPanel';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    CalendarIcon, Building2, DollarSign, CheckCircle2, XCircle,
    RefreshCw, ArrowUpDown, ChevronDown, ChevronUp, StickyNote, Sparkles,
    Save, Loader2, Search, MailSearch, AlertTriangle, Mail, UserCog
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ReassignUserDialog } from '../components/admin/ReassignUserDialog';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BANK_COLORS = {
    'TD Bank': { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-800' },
    'Wise': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800' },
    'Capital One': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' },
    'PNC': { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800' },
    'Cash App': { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800' },
    'PayPal': { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', badge: 'bg-indigo-100 text-indigo-800' },
    'Regions Bank': { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', badge: 'bg-teal-100 text-teal-800' },
    'Stripe': { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', badge: 'bg-violet-100 text-violet-800' },
    'Bank of America': { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-800' },
    'American Express': { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', badge: 'bg-cyan-100 text-cyan-800' },
    'Chase': { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', badge: 'bg-blue-100 text-blue-900' },
};
const DEFAULT_COLOR = { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', badge: 'bg-slate-100 text-slate-800' };

function getColorForBank(bankName) {
    for (const [key, val] of Object.entries(BANK_COLORS)) {
        if (bankName.toLowerCase().includes(key.toLowerCase())) return val;
    }
    return DEFAULT_COLOR;
}

function BankSection({ bankName, data, onDepositClick }) {
    const [expanded, setExpanded] = useState(true);
    const [sortKey, setSortKey] = useState('amount');
    const [sortAsc, setSortAsc] = useState(false);
    const colors = getColorForBank(bankName);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortAsc(!sortAsc);
        } else {
            setSortKey(key);
            setSortAsc(true);
        }
    };

    const parseTime12to24 = (t) => {
        if (!t) return 0;
        const match = t.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (!match) return 0;
        let h = parseInt(match[1], 10);
        const m = parseInt(match[2], 10);
        const pm = match[3].toUpperCase() === 'PM';
        if (pm && h !== 12) h += 12;
        if (!pm && h === 12) h = 0;
        return h * 60 + m;
    };

    const sorted = useMemo(() => {
        const list = [...data.deposits];
        list.sort((a, b) => {
            let va, vb;
            switch (sortKey) {
                case 'time':
                    va = parseTime12to24(a.time);
                    vb = parseTime12to24(b.time);
                    return sortAsc ? va - vb : vb - va;
                case 'sender_name':
                    va = (a.sender_name || '').toLowerCase();
                    vb = (b.sender_name || '').toLowerCase();
                    break;
                case 'amount':
                    return sortAsc ? a.amount - b.amount : b.amount - a.amount;
                case 'invoice':
                    va = a.has_invoice ? 1 : 0;
                    vb = b.has_invoice ? 1 : 0;
                    return sortAsc ? va - vb : vb - va;
                case 'user':
                    va = (a.invoice_user || '').toLowerCase();
                    vb = (b.invoice_user || '').toLowerCase();
                    break;
                default:
                    return 0;
            }
            if (va < vb) return sortAsc ? -1 : 1;
            if (va > vb) return sortAsc ? 1 : -1;
            return 0;
        });
        return list;
    }, [data.deposits, sortKey, sortAsc]);

    return (
        <Card className={`${colors.border} border shadow-sm`} data-testid={`bank-section-${bankName}`}>
            <CardHeader
                className={`${colors.bg} cursor-pointer py-3 px-4`}
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Building2 className={`w-5 h-5 ${colors.text}`} />
                        <CardTitle className={`text-base font-semibold ${colors.text}`}>
                            {bankName}
                        </CardTitle>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge}`}>
                            {data.count} {data.count === 1 ? 'deposito' : 'depositos'}
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-right">
                            <span className={`text-lg font-bold ${colors.text}`}>
                                ${data.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </span>
                            <span className="text-xs text-slate-500 ml-2">
                                ({data.matched_count}/{data.count} verificados)
                            </span>
                        </div>
                        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                </div>
            </CardHeader>
            {expanded && (
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-slate-50/50">
                                    <th className="text-left py-2 px-4 font-medium text-slate-500 cursor-pointer select-none"
                                        onClick={(e) => { e.stopPropagation(); toggleSort('time'); }}
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            Hora <ArrowUpDown className={`w-3 h-3 ${sortKey === 'time' ? 'text-sky-600' : ''}`} />
                                        </span>
                                    </th>
                                    <th className="text-left py-2 px-4 font-medium text-slate-500 cursor-pointer select-none"
                                        onClick={(e) => { e.stopPropagation(); toggleSort('sender_name'); }}
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            Remitente <ArrowUpDown className={`w-3 h-3 ${sortKey === 'sender_name' ? 'text-sky-600' : ''}`} />
                                        </span>
                                    </th>
                                    <th className="text-right py-2 px-4 font-medium text-slate-500 cursor-pointer select-none"
                                        onClick={(e) => { e.stopPropagation(); toggleSort('amount'); }}
                                    >
                                        <span className="inline-flex items-center gap-1 justify-end">
                                            Monto <ArrowUpDown className={`w-3 h-3 ${sortKey === 'amount' ? 'text-sky-600' : ''}`} />
                                        </span>
                                    </th>
                                    <th className="text-center py-2 px-4 font-medium text-slate-500 cursor-pointer select-none"
                                        onClick={(e) => { e.stopPropagation(); toggleSort('invoice'); }}
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            Factura <ArrowUpDown className={`w-3 h-3 ${sortKey === 'invoice' ? 'text-sky-600' : ''}`} />
                                        </span>
                                    </th>
                                    <th className="text-left py-2 px-4 font-medium text-slate-500 cursor-pointer select-none"
                                        onClick={(e) => { e.stopPropagation(); toggleSort('user'); }}
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            Usuario <ArrowUpDown className={`w-3 h-3 ${sortKey === 'user' ? 'text-sky-600' : ''}`} />
                                        </span>
                                    </th>
                                    <th className="text-center py-2 px-4 font-medium text-slate-500 w-10">Nota</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((dep, idx) => (
                                    <tr key={dep.email_id || idx}
                                        className={`border-b last:border-b-0 cursor-pointer transition-colors hover:bg-sky-50/60 ${dep.has_invoice ? 'bg-white' : 'bg-amber-50/40'}`}
                                        onClick={() => onDepositClick(dep, bankName)}
                                        data-testid={`deposit-row-${dep.email_id}`}
                                    >
                                        <td className="py-2 px-4 text-slate-600 whitespace-nowrap">{dep.time}</td>
                                        <td className="py-2 px-4 font-medium text-slate-800">{dep.sender_name}</td>
                                        <td className="py-2 px-4 text-right font-semibold text-slate-800">
                                            {dep.original_amount != null ? (
                                                <div>
                                                    <span className="text-xs text-slate-400 line-through mr-1">
                                                        ${dep.original_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                                    </span>
                                                    <span>${dep.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                </div>
                                            ) : (
                                                <span>${dep.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                            )}
                                        </td>
                                        <td className="py-2 px-4 text-center">
                                            {dep.has_invoice ? (
                                                <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                                    <CheckCircle2 className="w-3 h-3" /> Verificado
                                                </span>
                                            ) : dep.note ? (
                                                <span className="inline-flex items-center gap-1 text-xs text-sky-700 bg-sky-100 px-2 py-0.5 rounded-full">
                                                    <StickyNote className="w-3 h-3" /> Anotado
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                                    <XCircle className="w-3 h-3" /> Sin factura
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2 px-4 text-slate-600 text-sm">
                                            {dep.invoice_user ? dep.invoice_user : dep.suggested_users?.length > 0 ? (
                                                <span
                                                    className="inline-flex items-center gap-1 text-xs text-violet-700 bg-violet-50 border border-dashed border-violet-300 px-2 py-0.5 rounded-full cursor-help"
                                                    title={dep.suggested_users.map(u => `${u.user_name}: ${u.count} ${u.count === 1 ? 'vez' : 'veces'} (últ. ${u.last_at_display}, "${u.last_sender}")`).join('\n')}
                                                    data-testid={`suggested-user-${dep.email_id}`}
                                                >
                                                    <Sparkles className="w-3 h-3" />
                                                    Posible: {dep.suggested_users[0].user_name}
                                                    {dep.suggested_users[0].count > 1 && <span className="text-violet-500">×{dep.suggested_users[0].count}</span>}
                                                    {dep.suggested_users.length > 1 && <span className="text-violet-400">+{dep.suggested_users.length - 1}</span>}
                                                </span>
                                            ) : '—'}
                                        </td>
                                        <td className="py-2 px-4 text-center">
                                            {dep.note ? (
                                                <StickyNote className="w-4 h-4 text-amber-500 mx-auto" />
                                            ) : (
                                                <StickyNote className="w-4 h-4 text-slate-300 mx-auto" />
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            )}
        </Card>
    );
}

export default function DailyDeposits() {
    const { getAuthHeaders } = useAuth();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [calendarOpen, setCalendarOpen] = useState(false);

    // Tab state (deep-link: /dashboard/daily-deposits?tab=failed)
    const [activeTab, setActiveTab] = useState(
        () => new URLSearchParams(window.location.search).get('tab') === 'failed' ? 'failed' : 'deposits'
    );

    // Failed parsings state
    const [failedData, setFailedData] = useState(null);
    const [failedLoading, setFailedLoading] = useState(false);

    // Note modal state
    const [noteModalOpen, setNoteModalOpen] = useState(false);
    const [selectedDeposit, setSelectedDeposit] = useState(null);
    const [selectedBank, setSelectedBank] = useState('');
    const [noteText, setNoteText] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [unlinkInvoice, setUnlinkInvoice] = useState(false);
    const [reassign, setReassign] = useState(null);

    // Search filter
    const [searchQuery, setSearchQuery] = useState('');

    // Force rescan state
    const [rescanning, setRescanning] = useState(false);

    const fetchDeposits = async (date) => {
        setLoading(true);
        try {
            const dateStr = format(date, 'yyyy-MM-dd');
            const res = await axios.get(`${API}/api/admin/daily-deposits?date=${dateStr}`, {
                headers: getAuthHeaders()
            });
            setData(res.data);
        } catch (err) {
            console.error('Error fetching daily deposits:', err);
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchFailedParsings = async () => {
        setFailedLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/failed-parsings`, {
                headers: getAuthHeaders()
            });
            setFailedData(res.data);
        } catch (err) {
            console.error('Error fetching failed parsings:', err);
            setFailedData(null);
        } finally {
            setFailedLoading(false);
        }
    };

    useEffect(() => {
        fetchDeposits(selectedDate);
    }, [selectedDate]);

    useEffect(() => {
        if (activeTab === 'failed') {
            fetchFailedParsings();
        }
    }, [activeTab]);

    const handleDateSelect = (date) => {
        if (date) {
            setSelectedDate(date);
            setCalendarOpen(false);
        }
    };

    const handleForceRescan = async () => {
        setRescanning(true);
        try {
            const res = await axios.post(`${API}/api/force-rescan`, {}, {
                headers: getAuthHeaders(),
                timeout: 15000
            });
            toast.success(res.data.message || 'Re-escaneo iniciado...');
            // Refresh data progressively as the background scan finds emails
            setTimeout(() => fetchDeposits(selectedDate), 10000);
            setTimeout(() => fetchDeposits(selectedDate), 30000);
            setTimeout(() => { fetchDeposits(selectedDate); setRescanning(false); }, 60000);
        } catch (err) {
            console.error('Error force rescan:', err);
            toast.error('Error al re-escanear emails');
            setRescanning(false);
        }
    };

    const handleDepositClick = (deposit, bankName) => {
        setSelectedDeposit(deposit);
        setSelectedBank(bankName);
        setNoteText(deposit.note || '');
        setUnlinkInvoice(!!deposit.has_invoice);
        setNoteModalOpen(true);
    };

    const handleSaveNote = async () => {
        if (!selectedDeposit) return;
        setSavingNote(true);
        try {
            const doUnlink = unlinkInvoice && selectedDeposit.has_invoice;
            const res = await axios.put(`${API}/api/admin/deposit-notes`, {
                email_id: selectedDeposit.email_id,
                note: noteText,
                unlink_invoice: doUnlink,
            }, { headers: getAuthHeaders() });

            // Update local state
            setData(prev => {
                if (!prev) return prev;
                const updated = { ...prev, banks: { ...prev.banks } };
                for (const [bName, bData] of Object.entries(updated.banks)) {
                    const idx = bData.deposits.findIndex(d => d.email_id === selectedDeposit.email_id);
                    if (idx !== -1) {
                        updated.banks[bName] = {
                            ...bData,
                            deposits: bData.deposits.map((d, i) =>
                                i === idx
                                    ? { ...d, note: noteText, ...(doUnlink ? { has_invoice: false, invoice_user: null, invoice_name: null, invoice_id: null } : {}) }
                                    : d
                            ),
                            matched_count: doUnlink ? Math.max(0, (bData.matched_count || 0) - 1) : bData.matched_count
                        };
                        break;
                    }
                }
                return updated;
            });

            if (doUnlink) {
                const u = res.data.unlinked;
                toast.success(u ? `Nota guardada. Confirmación removida: ${u.invoice_name} (${u.invoice_user}) vuelve a pendiente. El depósito ya no se confirmará automáticamente.` : 'Nota guardada. Usuario eliminado del depósito; no se confirmará automáticamente.');
                const eid = selectedDeposit.email_id;
                setTimeout(async () => {
                    await fetchDeposits(selectedDate);
                    try {
                        const chk = await axios.get(`${API}/api/admin/daily-deposits`, { params: { date: format(selectedDate, 'yyyy-MM-dd') }, headers: getAuthHeaders() });
                        const still = Object.values(chk.data?.banks || {}).flatMap(b => b.deposits).find(d => d.email_id === eid);
                        if (still?.has_invoice) toast.error(`El depósito sigue marcado como verificado (${still.invoice_user}). Vuelve a intentarlo o avísame.`, { duration: 8000 });
                    } catch { /* ignore */ }
                }, 800);
            } else {
                toast.success('Nota guardada');
            }
            setNoteModalOpen(false);
        } catch (err) {
            console.error('Error saving note:', err);
            toast.error('Error al guardar la nota');
        } finally {
            setSavingNote(false);
        }
    };

    const bankEntries = useMemo(() => {
        if (!data?.banks) return [];
        const q = searchQuery.trim().toLowerCase();
        if (!q) return Object.entries(data.banks);

        const filtered = {};
        for (const [bankName, bankData] of Object.entries(data.banks)) {
            const matchingDeposits = bankData.deposits.filter(dep => {
                const nameMatch = (dep.sender_name || '').toLowerCase().includes(q);
                const amountMatch = String(dep.amount).includes(q);
                const userMatch = (dep.invoice_user || '').toLowerCase().includes(q);
                return nameMatch || amountMatch || userMatch;
            });
            if (matchingDeposits.length > 0) {
                filtered[bankName] = {
                    ...bankData,
                    deposits: matchingDeposits,
                    count: matchingDeposits.length,
                    total: matchingDeposits.reduce((sum, d) => sum + d.amount, 0),
                    matched_count: matchingDeposits.filter(d => d.has_invoice).length,
                };
            }
        }
        return Object.entries(filtered);
    }, [data, searchQuery]);

    return (
        <div className="space-y-6" data-testid="daily-deposits-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Depositos del Dia</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Registro de todos los depositos detectados por email
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {activeTab === 'deposits' && (
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Buscar nombre o monto..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-8 pr-3 py-2 w-[200px] text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300"
                                data-testid="deposit-search-input"
                            />
                        </div>
                    )}
                    {activeTab === 'deposits' && (
                        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="w-[200px] justify-start text-left font-normal"
                                    data-testid="deposit-date-picker"
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {format(selectedDate, "dd 'de' MMMM, yyyy", { locale: es })}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="end">
                                <Calendar
                                    mode="single"
                                    selected={selectedDate}
                                    onSelect={handleDateSelect}
                                    locale={es}
                                    defaultMonth={selectedDate}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    )}
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => activeTab === 'deposits' ? fetchDeposits(selectedDate) : fetchFailedParsings()}
                        disabled={loading || failedLoading}
                        data-testid="refresh-deposits-btn"
                        title="Actualizar datos"
                    >
                        <RefreshCw className={`w-4 h-4 ${(loading || failedLoading) ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                        variant="default"
                        onClick={handleForceRescan}
                        disabled={rescanning || loading}
                        data-testid="force-rescan-btn"
                        className="bg-sky-600 hover:bg-sky-700 text-white gap-2"
                    >
                        {rescanning ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <MailSearch className="w-4 h-4" />
                        )}
                        {rescanning ? 'Escaneando...' : 'Re-escanear Emails'}
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit" data-testid="deposits-tabs">
                <button
                    onClick={() => setActiveTab('deposits')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                        activeTab === 'deposits'
                            ? 'bg-white text-slate-800 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="tab-deposits"
                >
                    <DollarSign className="w-4 h-4" />
                    Depositos
                </button>
                <button
                    onClick={() => setActiveTab('failed')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                        activeTab === 'failed'
                            ? 'bg-white text-slate-800 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="tab-failed-parsings"
                >
                    <AlertTriangle className="w-4 h-4" />
                    Emails No Procesados
                    {failedData && failedData.total > 0 && (
                        <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">
                            {failedData.total}
                        </span>
                    )}
                </button>
            </div>

            {/* === DEPOSITS TAB === */}
            {activeTab === 'deposits' && (
                <>
                    {/* Summary Cards */}
                    {data && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <Card className="border-sky-200">
                                <CardContent className="p-4 text-center">
                                    <p className="text-xs text-slate-500 mb-1">Total Depositos</p>
                                    <p className="text-2xl font-bold text-sky-700">{data.grand_count}</p>
                                </CardContent>
                            </Card>
                            <Card className="border-sky-200">
                                <CardContent className="p-4 text-center">
                                    <p className="text-xs text-slate-500 mb-1">Monto Total</p>
                                    <p className="text-2xl font-bold text-sky-700">
                                        ${data.grand_total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="border-green-200">
                                <CardContent className="p-4 text-center">
                                    <p className="text-xs text-slate-500 mb-1">Con Factura</p>
                                    <p className="text-2xl font-bold text-green-700">{data.matched_total}</p>
                                </CardContent>
                            </Card>
                            <Card className="border-amber-200">
                                <CardContent className="p-4 text-center">
                                    <p className="text-xs text-slate-500 mb-1">Sin Factura</p>
                                    <p className="text-2xl font-bold text-amber-700">
                                        {data.grand_count - data.matched_total}
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* Loading state */}
                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <RefreshCw className="w-6 h-6 animate-spin text-sky-500 mr-3" />
                            <span className="text-slate-500">Cargando depositos...</span>
                        </div>
                    )}

                    {/* Bank sections */}
                    {!loading && data && bankEntries.length > 0 && (
                        <div className="space-y-4">
                            {bankEntries.map(([bankName, bankData]) => (
                                <BankSection
                                    key={bankName}
                                    bankName={bankName}
                                    data={bankData}
                                    onDepositClick={handleDepositClick}
                                />
                            ))}
                        </div>
                    )}

                    {/* Empty state */}
                    {!loading && data && bankEntries.length === 0 && (
                        <Card className="border-dashed border-2 border-slate-200">
                            <CardContent className="p-12 text-center">
                                {searchQuery.trim() ? (
                                    <>
                                        <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                        <p className="text-lg font-medium text-slate-500">
                                            No se encontraron resultados para &ldquo;{searchQuery}&rdquo;
                                        </p>
                                        <p className="text-sm text-slate-400 mt-1">
                                            Intenta con otro nombre o monto
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                        <p className="text-lg font-medium text-slate-500">
                                            No se encontraron depositos para esta fecha
                                        </p>
                                        <p className="text-sm text-slate-400 mt-1">
                                            Los depositos se detectan de los emails leídos por el sistema (últimos 30 días)
                                        </p>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Grand Total Footer */}
                    {!loading && data && bankEntries.length > 0 && (
                        <Card className="bg-slate-800 text-white border-0">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-slate-300">Resumen del {data.date_display}</p>
                                        <p className="text-xs text-slate-400 mt-0.5">
                                            {Object.keys(data.banks).length} bancos | {data.grand_count} depositos | {data.matched_total} verificados
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-bold">
                                            ${data.grand_total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </p>
                                        <p className="text-xs text-slate-400">Total del dia</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {/* === FAILED PARSINGS TAB === */}
            {activeTab === 'failed' && (
                <>
                    {failedLoading && (
                        <div className="flex items-center justify-center py-12">
                            <RefreshCw className="w-6 h-6 animate-spin text-sky-500 mr-3" />
                            <span className="text-slate-500">Cargando emails no procesados...</span>
                        </div>
                    )}

                    {!failedLoading && failedData && failedData.total > 0 && (
                        <div className="space-y-4">
                            {/* Summary */}
                            <Card className="border-amber-200">
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                                        <div>
                                            <p className="text-sm font-medium text-slate-800">
                                                {failedData.total} email{failedData.total !== 1 ? 's' : ''} con problemas de parsing (ultimos 7 dias)
                                            </p>
                                            <p className="text-xs text-slate-500 mt-0.5">
                                                Estos emails fueron detectados como notificaciones de pago pero no se pudo extraer toda la informacion
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Table */}
                            <Card className="border-slate-200 shadow-sm">
                                <CardContent className="p-0">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b bg-slate-50">
                                                    <th className="text-left py-2.5 px-4 font-medium text-slate-500">Fecha</th>
                                                    <th className="text-left py-2.5 px-4 font-medium text-slate-500">Asunto</th>
                                                    <th className="text-left py-2.5 px-4 font-medium text-slate-500">Remitente Email</th>
                                                    <th className="text-left py-2.5 px-4 font-medium text-slate-500">Banco</th>
                                                    <th className="text-right py-2.5 px-4 font-medium text-slate-500">Monto</th>
                                                    <th className="text-left py-2.5 px-4 font-medium text-slate-500">Nombre</th>
                                                    <th className="text-left py-2.5 px-4 font-medium text-slate-500">Problema</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {failedData.failures.map((f, idx) => (
                                                    <tr key={idx} className="border-b last:border-b-0 hover:bg-amber-50/40 transition-colors"
                                                        data-testid={`failed-parsing-row-${idx}`}
                                                    >
                                                        <td className="py-2 px-4 text-slate-600 whitespace-nowrap text-xs">
                                                            {f.date_display || '—'}
                                                        </td>
                                                        <td className="py-2 px-4 text-slate-800 max-w-[250px] truncate" title={f.subject}>
                                                            {f.subject || '—'}
                                                        </td>
                                                        <td className="py-2 px-4 text-slate-600 max-w-[200px] truncate text-xs" title={f.from}>
                                                            {f.from || '—'}
                                                        </td>
                                                        <td className="py-2 px-4">
                                                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                                                f.bank && f.bank !== 'No detectado'
                                                                    ? (getColorForBank(f.bank)?.badge || 'bg-slate-100 text-slate-700')
                                                                    : 'bg-red-100 text-red-700'
                                                            }`}>
                                                                {f.bank || 'No detectado'}
                                                            </span>
                                                        </td>
                                                        <td className="py-2 px-4 text-right font-medium text-slate-800">
                                                            {f.amount != null ? `$${f.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
                                                        </td>
                                                        <td className="py-2 px-4 text-slate-700">
                                                            {f.sender_name || <span className="text-red-500 text-xs">No extraido</span>}
                                                        </td>
                                                        <td className="py-2 px-4">
                                                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                                                                {f.failure_reason || 'Error de parsing'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {!failedLoading && failedData && failedData.total === 0 && (
                        <Card className="border-dashed border-2 border-slate-200">
                            <CardContent className="p-12 text-center">
                                <CheckCircle2 className="w-12 h-12 text-green-300 mx-auto mb-3" />
                                <p className="text-lg font-medium text-slate-500">
                                    No hay emails con problemas de parsing
                                </p>
                                <p className="text-sm text-slate-400 mt-1">
                                    Todos los emails de pago fueron procesados correctamente en los ultimos 7 dias
                                </p>
                            </CardContent>
                        </Card>
                    )}

                    {!failedLoading && !failedData && (
                        <Card className="border-dashed border-2 border-slate-200">
                            <CardContent className="p-12 text-center">
                                <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                <p className="text-lg font-medium text-slate-500">
                                    Haz clic en &ldquo;Re-escanear Emails&rdquo; para generar el reporte
                                </p>
                                <p className="text-sm text-slate-400 mt-1">
                                    Los fallos de parsing se detectan durante el escaneo de emails
                                </p>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {/* Note Modal */}
            <Dialog open={noteModalOpen} onOpenChange={setNoteModalOpen}>
                <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="deposit-note-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-slate-800">
                            <StickyNote className="w-5 h-5 text-amber-500" />
                            Nota del Deposito
                        </DialogTitle>
                    </DialogHeader>

                    {selectedDeposit && (
                        <div className="space-y-4">
                            {/* Deposit Info */}
                            <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Remitente:</span>
                                    <span className="font-medium text-slate-800">{selectedDeposit.sender_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Monto:</span>
                                    <span className="font-bold text-slate-800">
                                        ${selectedDeposit.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Banco:</span>
                                    <span className="text-slate-700">{selectedBank}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-500">Hora:</span>
                                    <span className="text-slate-700">{selectedDeposit.time}</span>
                                </div>
                                {selectedDeposit.has_invoice && (
                                    <div className="flex justify-between items-center gap-2">
                                        <span className="text-slate-500">Factura:</span>
                                        <span className="flex items-center gap-2">
                                            <span className="text-green-700">{selectedDeposit.invoice_user}</span>
                                            {selectedDeposit.invoice_id && (
                                                <button
                                                    type="button"
                                                    onClick={() => setReassign({ invoiceId: selectedDeposit.invoice_id, invoiceName: selectedDeposit.invoice_name || selectedDeposit.sender_name, amount: selectedDeposit.amount, currentUserName: selectedDeposit.invoice_user })}
                                                    className="inline-flex items-center gap-1 text-xs text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 hover:bg-fuchsia-100 px-2 py-0.5 rounded-full"
                                                    data-testid="deposit-reassign-btn"
                                                >
                                                    <UserCog className="w-3 h-3" /> Cambiar usuario
                                                </button>
                                            )}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {!selectedDeposit.has_invoice && selectedDeposit.suggested_users?.length > 0 && (
                                <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm" data-testid="deposit-suggestions">
                                    <p className="font-semibold text-violet-800 flex items-center gap-1.5 mb-1.5">
                                        <Sparkles className="w-4 h-4" /> Posibles usuarios (historial 6 meses)
                                    </p>
                                    <ul className="space-y-1">
                                        {selectedDeposit.suggested_users.map((u, i) => (
                                            <li key={i} className="flex justify-between gap-2 text-violet-900">
                                                <span><b>{u.user_name}</b> · {u.count} {u.count === 1 ? 'vez' : 'veces'}</span>
                                                <span className="text-xs text-violet-600 text-right">últ. {u.last_at_display} · "{u.last_sender}"{u.last_amount ? ` $${Number(u.last_amount).toFixed(2)}` : ''}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Note textarea */}
                            <Textarea
                                placeholder="Escribe una nota para este deposito..."
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                rows={4}
                                className="resize-none"
                                data-testid="deposit-note-textarea"
                            />

                            {selectedDeposit.has_invoice && (
                                <label className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 cursor-pointer" data-testid="deposit-unlink-label">
                                    <Checkbox
                                        checked={unlinkInvoice}
                                        onCheckedChange={(v) => setUnlinkInvoice(!!v)}
                                        className="mt-0.5"
                                        data-testid="deposit-unlink-checkbox"
                                    />
                                    <span className="text-sm text-red-800">
                                        <b>Quitar confirmación</b> de <b>{selectedDeposit.invoice_name}</b> ({selectedDeposit.invoice_user}).
                                        Se libera de todos lados: el depósito queda "Sin factura", la factura vuelve a Pendiente (solo confirmación manual) y puedes editarla o confirmarla de nuevo.
                                    </span>
                                </label>
                            )}

                            {!selectedDeposit.has_invoice && selectedDeposit.email_id && (
                                <AssignDepositPanel
                                    deposit={selectedDeposit}
                                    onAssigned={() => { setNoteModalOpen(false); fetchDeposits(selectedDate); }}
                                />
                            )}

                            {selectedDeposit.note_updated_at && (
                                <p className="text-xs text-slate-400">
                                    Ultima edicion: {selectedDeposit.note_updated_at}
                                </p>
                            )}
                        </div>
                    )}

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            variant="outline"
                            onClick={() => setNoteModalOpen(false)}
                            data-testid="deposit-note-cancel"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSaveNote}
                            disabled={savingNote}
                            className="bg-sky-600 hover:bg-sky-700"
                            data-testid="deposit-note-save"
                        >
                            {savingNote ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            Guardar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <ReassignUserDialog target={reassign} onClose={() => setReassign(null)} onDone={() => { setNoteModalOpen(false); fetchDeposits(selectedDate); }} />
        </div>
    );
}
